import { useCallback, useEffect, useRef, useState } from "react";
import { T, injectGlobalStyles } from "../lib/theme";
import { API, apiFetch } from "../lib/api";
import { cleanPhone, formatDate, formatTime, timeAgo } from "../lib/utils";
import { Btn, Icon } from "../components";
import useIsMobile from "../hooks/useIsMobile";

// ── Avatar ────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  ["rgba(99,102,241,0.22)",  "#6366f1"],
  ["rgba(139,92,246,0.22)",  "#8b5cf6"],
  ["rgba(56,189,248,0.18)",  "#38bdf8"],
  ["rgba(20,184,166,0.18)",  "#14b8a6"],
  ["rgba(245,158,11,0.18)",  "#f59e0b"],
  ["rgba(244,63,94,0.18)",   "#f43f5e"],
];
const NUMBER_COLORS = [
  "#6366f1", "#14b8a6", "#f59e0b", "#f43f5e", "#38bdf8", "#8b5cf6", "#22c55e",
];
const Avatar = ({ name, size = 44 }) => {
  const idx = (name || "?").charCodeAt(0) % AVATAR_COLORS.length;
  const [bg, fg] = AVATAR_COLORS[idx];
  return (
    <div style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.38), fontWeight: 700, color: fg, flexShrink: 0, letterSpacing: "-0.02em" }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
};

const ChatAttachment = ({ attachment, token }) => {
  const [src, setSrc] = useState("");
  const [open, setOpen] = useState(false);
  const isImage = attachment?.mimeType?.startsWith("image/");
  useEffect(() => {
    if (!isImage || !attachment?.filePath) return undefined;
    let active = true;
    let objectUrl = "";
    const filename = String(attachment.filePath).split(/[\\/]/).pop();
    fetch(`${API}/api/chat-media/${encodeURIComponent(filename)}`, { headers:{ Authorization:token } })
      .then(res => { if (!res.ok) throw new Error("Image unavailable"); return res.blob(); })
      .then(blob => { if (active) { objectUrl = URL.createObjectURL(blob); setSrc(objectUrl); } })
      .catch(() => {});
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment?.filePath, isImage, token]);

  if (isImage) return (
    <>
      <button type="button" onClick={()=>src&&setOpen(true)} title={attachment.label || "Open image"}
        style={{padding:0,border:`1px solid ${T.border2}`,borderRadius:10,overflow:"hidden",background:T.bg0,cursor:src?"zoom-in":"default",width:180,height:135,display:"flex",alignItems:"center",justifyContent:"center",color:T.text3}}>
        {src ? <img src={src} alt={attachment.label || "Chat attachment"} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : "Image unavailable"}
      </button>
      {open && <div onClick={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,.86)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,cursor:"zoom-out"}}>
        <img src={src} alt={attachment.label || "Chat attachment"} style={{maxWidth:"95vw",maxHeight:"90vh",objectFit:"contain",borderRadius:12}}/>
      </div>}
    </>
  );

  return <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,color:T.text2,background:"rgba(99,102,241,0.14)",border:"1px solid rgba(99,102,241,0.28)",borderRadius:8,padding:"3px 8px"}}>📎 {attachment?.label || "attachment"}</span>;
};

// ── CSS injections ────────────────────────────────────────────────────
const injectChatCSS = () => {
  if (document.getElementById("wb-chat-css")) return;
  const s = document.createElement("style");
  s.id = "wb-chat-css";
  s.textContent = `
    @keyframes wbTypingBounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
      30%            { transform: translateY(-6px); opacity: 1; }
    }
    .wb-typing-dot {
      border-radius: 50%;
      display: inline-block;
      animation: wbTypingBounce 1.2s ease infinite;
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(24px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(s);
};
const TypingDots = ({ color = "#94a3b8", size = 7 }) => {
  injectChatCSS();
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: Math.ceil(size * 0.7) }}>
      {[0, 1, 2].map(i => (
        <span key={i} className="wb-typing-dot" style={{ width: size, height: size, background: color, animationDelay: `${i * 0.18}s` }} />
      ))}
    </span>
  );
};

// ── Group messages by date ────────────────────────────────────────────
const groupMessages = (msgs) => {
  const out = [];
  let curDate = null;
  msgs.forEach(m => {
    const d = formatDate(m.timestamp);
    if (d !== curDate) { curDate = d; out.push({ date: d, msgs: [] }); }
    out[out.length - 1].msgs.push(m);
  });
  return out;
};


// ── Chat contact card ─────────────────────────────────────────────────
const ChatCard = ({ c, isActive, onClick, typingContacts, numbers }) => {
  const name    = c.contactName || cleanPhone(c.contact);
  const isTyping = typingContacts[c.contact];
  const showBadge = numbers.length > 1;
  const numEntry  = showBadge
    ? (c.numberId ? numbers.find(n => n.id === c.numberId) : numbers.find(n => n.id === "primary"))
    : null;
  const numLabel = numEntry?.label || "";
  const numColor = numEntry ? NUMBER_COLORS[numbers.indexOf(numEntry) % NUMBER_COLORS.length] : NUMBER_COLORS[0];

  return (
    <div onClick={onClick} className="card-hover"
      style={{
        background: isActive ? "rgba(99,102,241,0.09)" : T.bg2,
        border: `1px solid ${isActive ? "rgba(99,102,241,0.40)" : T.border1}`,
        borderRadius: 14, padding: "13px 14px", cursor: "pointer",
        transition: "all 0.18s", marginBottom: 8, position: "relative",
        boxShadow: isActive ? `0 0 0 2px rgba(99,102,241,0.18)` : "none",
      }}>
      <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
        <div style={{ position: "relative" }}>
          <Avatar name={name} size={44} />
          {/* Online / mode dot */}
          <span style={{
            position: "absolute", bottom: 1, right: 1,
            width: 10, height: 10, borderRadius: "50%",
            background: c.isHumanMode ? "#f59e0b" : "#22c55e",
            border: `2px solid ${T.bg2}`,
          }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, overflow: "hidden" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
              {showBadge && numLabel && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 5, background: numColor + "22", color: numColor, border: `1px solid ${numColor}44`, flexShrink: 0, letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                  {numLabel}
                </span>
              )}
            </div>
            <span style={{ fontSize: 10, color: T.text3, flexShrink: 0 }}>{timeAgo(c.lastMessageAt)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
            {isTyping ? (
              <span style={{ display: "flex", alignItems: "center", gap: 5, flex: 1 }}>
                <TypingDots color="#6366f1" size={4} />
                <span style={{ fontSize: 12, color: "#6366f1" }}>typing...</span>
              </span>
            ) : (
              <span style={{ fontSize: 12, color: T.text3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {c.lastMessage || "—"}
              </span>
            )}
            <div style={{ display: "flex", gap: 3, flexShrink: 0, alignItems: "center" }}>
              {c.isHumanMode && (
                <span style={{ fontSize: 9, background: "rgba(245,158,11,0.14)", color: "#f59e0b", padding: "2px 6px", borderRadius: 5, fontWeight: 700, letterSpacing: "0.04em" }}>HUMAN</span>
              )}
              {c.unreadCount > 0 && (
                <span style={{ background: "#6366f1", color: "#fff", borderRadius: 10, fontSize: 10, padding: "2px 7px", fontWeight: 800, minWidth: 18, textAlign: "center" }}>
                  {c.unreadCount > 99 ? "99+" : c.unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Contact profile side panel ────────────────────────────────────────
const ContactProfilePanel = ({ contact, contactName, token, onClose, onDeleteContact }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contact) return;
    setLoading(true);
    apiFetch(`/api/contacts/${encodeURIComponent(contact)}`, {}, token)
      .then(d => setProfile(d.profile || null))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [contact, token]);

  const phone = cleanPhone(contact);
  const displayName = profile?.name || contactName || phone;

  const statusColors = {
    cold: "#64748b", warm: "#f59e0b", hot: "#f43f5e",
    converted: "#6366f1", lost: "#9ca3af",
  };

  return (
    <div style={{
      width: 280, flexShrink: 0, borderLeft: `1px solid ${T.border1}`,
      background: T.bg1, display: "flex", flexDirection: "column",
      animation: "slideInRight 0.22s ease",
    }}>
      {/* Panel header */}
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border1}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>Contact Profile</span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, padding: 4, borderRadius: 6, display: "flex" }}
          onMouseOver={e => (e.currentTarget.style.color = T.text1)}
          onMouseOut={e  => (e.currentTarget.style.color = T.text3)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}>
            <div style={{ width: 22, height: 22, border: `2px solid ${T.border2}`, borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          </div>
        ) : (
          <>
            {/* Avatar + name */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 20 }}>
              <Avatar name={displayName} size={64} />
              <div style={{ marginTop: 12, fontSize: 16, fontWeight: 700, color: T.text1 }}>{displayName}</div>
              <div style={{ fontSize: 12, color: T.text3, marginTop: 3 }}>{phone}</div>
              {profile?.leadStatus && (
                <span style={{ marginTop: 8, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: (statusColors[profile.leadStatus] || "#9ca3af") + "22", color: statusColors[profile.leadStatus] || "#9ca3af", border: `1px solid ${(statusColors[profile.leadStatus] || "#9ca3af")}44` }}>
                  {profile.leadStatus.charAt(0).toUpperCase() + profile.leadStatus.slice(1)}
                </span>
              )}
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
              {[
                { label: "Pipeline", value: profile?.pipelineType === "ecommerce" ? "E-Commerce" : profile?.pipelineType === "lead_gen" ? "Lead Gen" : "—" },
                { label: "Stage", value: profile?.pipelineStage ? profile.pipelineStage.replace(/_/g, " ") : "—" },
                { label: "First seen", value: profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : "—" },
                { label: "Last active", value: profile?.lastContactedAt ? timeAgo(profile.lastContactedAt) : "—" },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: T.bg0, borderRadius: 10, padding: "10px 12px", border: `1px solid ${T.border1}` }}>
                  <div style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Tags */}
            {profile?.tags?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 7 }}>Tags</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {profile.tags.map(tag => (
                    <span key={tag} style={{ padding: "3px 8px", borderRadius: 20, background: "rgba(139,92,246,0.12)", color: "#8b5cf6", fontSize: 11, fontWeight: 600, border: "1px solid rgba(139,92,246,0.2)" }}>{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {profile?.notes && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 7 }}>Notes</div>
                <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6, background: T.bg0, borderRadius: 10, padding: "10px 12px", border: `1px solid ${T.border1}` }}>
                  {profile.notes}
                </div>
              </div>
            )}

            {!profile && (
              <div style={{ textAlign: "center", color: T.text3, fontSize: 13, paddingTop: 10 }}>
                No profile data found
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete button */}
      <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.border1}` }}>
        <button onClick={onDeleteContact}
          style={{ width: "100%", padding: "9px 14px", background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", borderRadius: 10, color: "#f43f5e", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: "all 0.15s" }}
          onMouseOver={e => { e.currentTarget.style.background = "rgba(244,63,94,0.15)"; e.currentTarget.style.borderColor = "rgba(244,63,94,0.4)"; }}
          onMouseOut={e  => { e.currentTarget.style.background = "rgba(244,63,94,0.08)"; e.currentTarget.style.borderColor = "rgba(244,63,94,0.25)"; }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
          Delete Contact + Chat
        </button>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────
export default function ChatsPage({ token, chats, setChats, socket, showToast }) {
  const isMobile = useIsMobile();
  const [selected,       setSelected]       = useState(null);
  const [messages,       setMessages]       = useState([]);
  const [isHumanMode,    setIsHumanMode]    = useState(false);
  const [agentNote,      setAgentNote]      = useState("");
  const [contactName,    setContactName]    = useState("");
  const [replyText,      setReplyText]      = useState("");
  const [loading,        setLoading]        = useState(false);
  const [sending,        setSending]        = useState(false);
  const [search,         setSearch]         = useState("");
  const [confirmClear,   setConfirmClear]   = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [showProfile,    setShowProfile]    = useState(false);
  const [typingContacts, setTypingContacts] = useState({});
  const [attachedFile,   setAttachedFile]   = useState(null);
  const [numbers,        setNumbers]        = useState([]);
  const [numberFilter,   setNumberFilter]   = useState("all");
  const endRef    = useRef(null);
  const inputRef  = useRef(null);
  const fileRef   = useRef(null);

  useEffect(() => { injectGlobalStyles(); injectChatCSS(); }, []);

  useEffect(() => {
    apiFetch("/api/whatsapp/numbers", {}, token)
      .then(d => setNumbers(d.numbers || []))
      .catch(() => {});
  }, [token]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (typingContacts[selected]) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [typingContacts, selected]);

  // Live socket updates
  useEffect(() => {
    if (!socket) return;
    const onMsg = (data) => {
      if (data.contact === selected) {
        const incoming = [data.message, data.reply].filter(Boolean);
        setMessages(prev => {
          const next = [...prev];
          for (const message of incoming) {
            const key = message.waMessageId || message.clientMessageId || message._id;
            const index = key ? next.findIndex(m => (m.waMessageId || m.clientMessageId || m._id) === key) : -1;
            if (index >= 0) next[index] = { ...next[index], ...message };
            else next.push(message);
          }
          return next;
        });
        if (data.isHumanMode !== undefined) setIsHumanMode(data.isHumanMode);
      }
      setChats(prev => {
        const existing = prev.find(c => c.contact === data.contact);
        const lastMessage = data.reply?.content || data.message?.content || existing?.lastMessage || "";
        const lastMessageAt = new Date().toISOString();
        if (!existing) {
          return [{
            contact: data.contact,
            contactName: data.contactName || cleanPhone(data.contact),
            lastMessage,
            lastMessageAt,
            unreadCount: data.contact === selected ? 0 : (data.updateOnly ? 0 : 1),
            isHumanMode: data.isHumanMode ?? false,
            numberId: data.numberId || null,
          }, ...prev];
        }
        return prev.map(c => c.contact === data.contact ? {
          ...c,
          lastMessage,
          lastMessageAt,
          unreadCount: c.contact === selected ? 0 : data.updateOnly ? (c.unreadCount || 0) : (c.unreadCount || 0) + 1,
          isHumanMode: data.isHumanMode ?? c.isHumanMode,
        } : c);
      });
    };
    const onMode = (d) => { if (d.contact === selected) setIsHumanMode(d.isHumanMode); };
    const onBotResumed = (d) => {
      if (d.contact === selected) {
        setIsHumanMode(false);
        setAgentNote(d.agentNote || "");
        setChats(prev => prev.map(c => c.contact === d.contact ? { ...c, isHumanMode: false } : c));
      }
    };
    const onTyping = (data) => {
      const contact = data.contact;
      if (!contact) return;
      if (data.isTyping) {
        setTypingContacts(prev => ({ ...prev, [contact]: true }));
        setTimeout(() => {
          setTypingContacts(prev => { const n = { ...prev }; delete n[contact]; return n; });
        }, 8000);
      } else {
        setTypingContacts(prev => { const n = { ...prev }; delete n[contact]; return n; });
      }
    };
    const onMessageStatus = (data) => {
      if (data.contact !== selected || !data.waMessageId) return;
      setMessages(prev => prev.map(m => m.waMessageId === data.waMessageId
        ? { ...m, status: data.status, statusUpdatedAt: data.statusUpdatedAt }
        : m));
    };
    socket.on("new_message",    onMsg);
    socket.on("mode_changed",   onMode);
    socket.on("bot_resumed",    onBotResumed);
    socket.on("contact_typing", onTyping);
    socket.on("message_status", onMessageStatus);
    return () => {
      socket.off("new_message",    onMsg);
      socket.off("mode_changed",   onMode);
      socket.off("bot_resumed",    onBotResumed);
      socket.off("contact_typing", onTyping);
      socket.off("message_status", onMessageStatus);
    };
  }, [socket, selected]);

  const loadChat = useCallback(async (contact) => {
    setSelected(contact);
    setLoading(true);
    setMessages([]);
    setReplyText("");
    setShowProfile(false);
    setAgentNote("");
    try {
      const d = await apiFetch(`/api/chats/${encodeURIComponent(contact)}`, {}, token);
      setMessages(d.messages || []);
      setIsHumanMode(d.isHumanMode || false);
      setAgentNote(d.agentNote || "");
      setContactName(d.contactName || cleanPhone(contact));
      setChats(p => p.map(c => c.contact === contact ? { ...c, unreadCount: 0 } : c));
    } catch { showToast?.("Failed to load messages", "error"); }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [token]);

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !selected || sending) return;
    setReplyText("");
    setSending(true);
    const clientMessageId = window.crypto?.randomUUID?.() || `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimistic = { role: "assistant", content: text, timestamp: new Date().toISOString(), clientMessageId, direction: "outbound", source: "dashboard", status: "pending" };
    setMessages(p => [...p, optimistic]);
    try {
      const result = await apiFetch(`/api/chats/${encodeURIComponent(selected)}/send`, {
        method: "POST", body: JSON.stringify({ text, clientMessageId }),
      }, token);
      if (result?.message) setMessages(p => p.map(m => m.clientMessageId === clientMessageId ? { ...m, ...result.message } : m));
    } catch {
      setMessages(p => p.map(m => m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m));
      showToast?.("Send failed — check WhatsApp connection", "error");
    }
    setSending(false);
  };

  const sendMedia = async () => {
    if (!attachedFile || !selected || sending) return;
    setSending(true);
    const caption = replyText.trim();
    const label   = attachedFile.type.startsWith("image/") ? "Image" : "Document";
    const clientMessageId = window.crypto?.randomUUID?.() || `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimistic = {
      role: "assistant",
      content: caption ? `[${label}: ${attachedFile.name}] ${caption}` : `[${label}: ${attachedFile.name}]`,
      timestamp: new Date().toISOString(),
      clientMessageId,
      direction: "outbound",
      source: "dashboard",
      status: "pending",
    };
    setMessages(p => [...p, optimistic]);
    setAttachedFile(null);
    setReplyText("");
    try {
      const form = new FormData();
      form.append("file", attachedFile);
      form.append("clientMessageId", clientMessageId);
      if (caption) form.append("caption", caption);
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await fetch(`${API_BASE}/api/chats/${encodeURIComponent(selected)}/send-media`, {
        method: "POST",
        headers: { Authorization: token },
        body: form,
      });
      if (!res.ok) throw new Error("failed");
      const result = await res.json();
      if (result?.message) setMessages(p => p.map(m => m.clientMessageId === clientMessageId ? { ...m, ...result.message } : m));
    } catch {
      setMessages(p => p.map(m => m.clientMessageId === clientMessageId ? { ...m, status: "failed" } : m));
      showToast?.("Failed to send file — check WhatsApp connection", "error");
    }
    setSending(false);
  };

  const toggleMode = async () => {
    try {
      const d = await apiFetch(`/api/chats/${encodeURIComponent(selected)}/toggle-mode`, { method: "POST" }, token);
      setIsHumanMode(d.isHumanMode);
      setAgentNote("");
      setChats(p => p.map(c => c.contact === selected ? { ...c, isHumanMode: d.isHumanMode } : c));
      showToast?.(d.isHumanMode ? "Switched to human mode — you're replying now" : "AI resumed auto-replies");
    } catch { showToast?.("Failed to toggle mode", "error"); }
  };

  const clearChat = async () => {
    try {
      await apiFetch(`/api/chats/${encodeURIComponent(selected)}`, { method: "DELETE" }, token);
      setMessages([]);
      setConfirmClear(false);
      showToast?.("Chat cleared");
    } catch { showToast?.("Failed to clear chat", "error"); }
  };

  const deleteContactAndChat = async () => {
    setDeleting(true);
    try {
      await apiFetch(`/api/chats/${encodeURIComponent(selected)}`, { method: "DELETE" }, token).catch(() => {});
      await apiFetch(`/api/contacts/${encodeURIComponent(selected)}`, { method: "DELETE" }, token).catch(() => {});
      setChats(prev => prev.filter(c => c.contact !== selected));
      setSelected(null);
      setMessages([]);
      setConfirmDelete(false);
      setShowProfile(false);
      showToast?.("Contact and chat deleted");
    } catch {
      showToast?.("Delete failed", "error");
    }
    setDeleting(false);
  };

  const filtered = chats.filter(c => {
    const matchSearch = (c.contactName || cleanPhone(c.contact)).toLowerCase().includes(search.toLowerCase()) ||
      cleanPhone(c.contact).includes(search);
    if (!matchSearch) return false;
    if (numberFilter === "all") return true;
    if (numberFilter === "primary") return !c.numberId;
    return c.numberId === numberFilter;
  });

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

      {/* ── Left sidebar: card list ──────────────────────────────── */}
      <div style={{ width: isMobile ? "100%" : 300, flexShrink: 0, borderRight: `1px solid ${T.border1}`, display: isMobile && selected ? "none" : "flex", flexDirection: "column", background: T.bg1 }}>
        {/* Header */}
        <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text1, marginBottom: 10, letterSpacing: "-0.01em" }}>Chats</div>
          {/* Search */}
          <div style={{ position: "relative" }}>
            <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..."
              style={{ width: "100%", padding: "8px 12px 8px 32px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, color: T.text1, fontSize: 12, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
              onFocus={e => (e.target.style.borderColor = "#6366f1")}
              onBlur={e  => (e.target.style.borderColor = T.border1)} />
          </div>
          {numbers.length > 1 && (
            <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
              {[{ id: "all", label: "All", colorIdx: -1 }, ...numbers].map((n, i) => {
                const isActive = numberFilter === n.id;
                const color = i === 0 ? "#6366f1" : NUMBER_COLORS[(i - 1) % NUMBER_COLORS.length];
                return (
                  <button key={n.id} onClick={() => setNumberFilter(n.id)}
                    style={{
                      padding: "3px 9px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontWeight: isActive ? 700 : 500,
                      border: `1px solid ${isActive ? color : T.border1}`,
                      background: isActive ? color + "22" : "transparent",
                      color: isActive ? color : T.text3,
                      display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s",
                    }}>
                    {i > 0 && <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />}
                    {n.label}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 11, color: T.text3, marginTop: 8, paddingLeft: 1 }}>
            {filtered.length} conversation{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Card list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}>
          {!filtered.length && (
            <div style={{ padding: "40px 16px", textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, opacity: 0.15 }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.4"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <div style={{ fontSize: 13, color: T.text3, fontWeight: 600 }}>
                {search ? "No contacts found" : "No conversations yet"}
              </div>
              {!search && (
                <div style={{ fontSize: 12, color: T.text3, marginTop: 6, lineHeight: 1.6 }}>
                  Messages appear here once someone writes to your WhatsApp number
                </div>
              )}
            </div>
          )}
          {filtered.map(c => (
            <ChatCard
              key={c.contact}
              c={c}
              isActive={selected === c.contact}
              onClick={() => loadChat(c.contact)}
              typingContacts={typingContacts}
              numbers={numbers}
            />
          ))}
        </div>
      </div>

      {/* ── Chat area ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: isMobile && !selected ? "none" : "flex", flexDirection: "column", background: T.bg0, overflow: "hidden", position: "relative" }}>
        {!selected ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, color: T.text3 }}>
            <div style={{ width: 80, height: 80, borderRadius: 22, background: T.bg2, border: `1px solid ${T.border1}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="chats" size={32} color={T.text3} />
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text2 }}>Select a conversation</div>
              <div style={{ fontSize: 13, color: T.text3, marginTop: 5 }}>Pick a contact from the left to view messages</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
              {/* Chat header */}
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border1}`, display: "flex", alignItems: "center", gap: 12, background: T.bg1, flexShrink: 0 }}>
                {/* Back button on mobile */}
                {isMobile && (
                  <button onClick={() => setSelected(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: T.text2, padding: "4px 2px", display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                )}
                <button onClick={() => setShowProfile(p => !p)} title="View profile"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}>
                  <Avatar name={contactName || cleanPhone(selected)} size={40} />
                </button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>{contactName || cleanPhone(selected)}</div>
                  <div style={{ fontSize: 11, marginTop: 2, display: "flex", alignItems: "center", gap: 5, color: typingContacts[selected] ? "#38bdf8" : isHumanMode ? "#f59e0b" : "#22c55e" }}>
                    {typingContacts[selected] ? (
                      <><TypingDots color="#38bdf8" size={4} /><span style={{ fontWeight: 500 }}>typing...</span></>
                    ) : (
                      <><span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", animation: !isHumanMode ? "blink 2s infinite" : "none" }} />
                      {isHumanMode ? "You are replying — AI paused" : "AI is auto-replying"}</>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {/* Profile toggle */}
                  <button onClick={() => setShowProfile(p => !p)} title="View contact profile"
                    style={{ background: showProfile ? "rgba(99,102,241,0.15)" : "none", border: `1px solid ${showProfile ? "rgba(99,102,241,0.4)" : T.border2}`, cursor: "pointer", color: showProfile ? "#6366f1" : T.text3, padding: "6px 10px", borderRadius: 9, display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, transition: "all 0.15s" }}
                    onMouseOver={e => { if (!showProfile) { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#6366f1"; } }}
                    onMouseOut={e  => { if (!showProfile) { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = T.text3; } }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    Profile
                  </button>
                  {/* Human / AI toggle */}
                  <Btn variant={isHumanMode ? "primary" : "ghost"} size="sm" onClick={toggleMode} style={isHumanMode ? {} : { border: `1px solid ${T.border2}` }}>
                    <Icon name={isHumanMode ? "robot" : "user"} size={13} color={isHumanMode ? "#fff" : T.text2} />
                    {isHumanMode ? "Resume AI" : "Take over"}
                  </Btn>
                  {/* Clear chat */}
                  <button onClick={() => setConfirmClear(true)} title="Clear chat history"
                    style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, padding: 6, borderRadius: 8, display: "flex", transition: "color 0.15s" }}
                    onMouseOver={e => (e.currentTarget.style.color = "#f59e0b")}
                    onMouseOut={e  => (e.currentTarget.style.color = T.text3)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 8px", display: "flex", flexDirection: "column" }}>
                {loading && (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 26, height: 26, border: `2px solid ${T.border2}`, borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  </div>
                )}
                {!loading && !messages.length && (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.text3, fontSize: 13 }}>
                    No messages yet
                  </div>
                )}
                {!loading && groupMessages(messages).map((grp, gi) => (
                  <div key={gi}>
                    <div style={{ textAlign: "center", margin: "14px 0 10px" }}>
                      <span style={{ fontSize: 11, color: T.text3, background: T.bg1, padding: "3px 12px", borderRadius: 20, border: `1px solid ${T.border1}` }}>{grp.date}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {grp.msgs.map((m, mi) => (
                        <div key={m.waMessageId || m.clientMessageId || m._id || mi} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-start" : "flex-end", animation: "fadeUp 0.18s ease" }}>
                          <div style={{ maxWidth: "72%", padding: "9px 13px", borderRadius: m.role === "user" ? "14px 14px 14px 4px" : "14px 14px 4px 14px", background: m.role === "user" ? T.bg2 : "linear-gradient(135deg,rgba(99,102,241,0.16),rgba(99,102,241,0.07))", border: `1px solid ${m.role === "user" ? T.border2 : "rgba(99,102,241,0.22)"}`, color: T.text1, fontSize: 13, lineHeight: 1.65, wordBreak: "break-word" }}>
                            {m.content}
                            {Array.isArray(m.media) && m.media.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: m.content ? 6 : 0 }}>
                                {m.media.map((att, ai) => (
                                  <ChatAttachment key={ai} attachment={att} token={token}/>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: T.text3, marginTop: 3, padding: "0 3px" }}>
                            {m.role === "user" ? "Customer" : "Bot"} · {formatTime(m.timestamp)}{m.role !== "user" && m.status ? ` · ${m.status}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {/* Bot-resumed banner */}
                {agentNote && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "6px 0", animation: "fadeUp 0.2s ease" }}>
                    <div style={{ flex: 1, padding: "8px 13px", borderRadius: 10, background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.25)", fontSize: 12, color: "#a5b4fc", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 700, color: "#6366f1" }}>AI resumed — </span>
                      {agentNote.replace(/^Bot resumed — /, "")}
                    </div>
                    <button onClick={() => setAgentNote("")} title="Dismiss"
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, padding: "6px 4px", fontSize: 14, lineHeight: 1 }}>×</button>
                  </div>
                )}

                {/* Typing bubble */}
                {typingContacts[selected] && (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginTop: 4, animation: "fadeUp 0.25s ease" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.text2, flexShrink: 0 }}>
                      {(contactName || cleanPhone(selected))[0]?.toUpperCase()}
                    </div>
                    <div style={{ padding: "12px 18px", borderRadius: "18px 18px 18px 4px", background: T.bg2, border: `1px solid ${T.border2}`, display: "flex", alignItems: "center", minWidth: 62 }}>
                      <TypingDots size={7} />
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Reply input */}
              {isHumanMode ? (
                <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border1}`, background: T.bg1, flexShrink: 0 }}>
                  {/* File preview bar */}
                  {attachedFile && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "7px 10px", borderRadius: 9, background: T.bg2, border: `1px solid ${T.border1}` }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span style={{ fontSize: 12, color: T.text1, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {attachedFile.name}
                      </span>
                      <span style={{ fontSize: 11, color: T.text3, flexShrink: 0 }}>
                        {(attachedFile.size / 1024).toFixed(0)} KB
                      </span>
                      <button onClick={() => setAttachedFile(null)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: T.text3, padding: 2, display: "flex" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    {/* Hidden file input */}
                    <input ref={fileRef} type="file"
                      accept="image/*,application/pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.pptx"
                      style={{ display: "none" }}
                      onChange={e => { if (e.target.files[0]) setAttachedFile(e.target.files[0]); e.target.value = ""; }}
                    />
                    {/* Attach button */}
                    <button onClick={() => fileRef.current?.click()} title="Attach file"
                      style={{ background: attachedFile ? T.indigoDim : "none", border: `1px solid ${attachedFile ? T.indigo : T.border2}`, borderRadius: 9, cursor: "pointer", color: attachedFile ? T.indigo : T.text3, padding: "8px 10px", display: "flex", alignItems: "center", flexShrink: 0, transition: "all 0.15s" }}
                      onMouseOver={e => { if (!attachedFile) { e.currentTarget.style.borderColor = T.indigo; e.currentTarget.style.color = T.indigo; } }}
                      onMouseOut={e  => { if (!attachedFile) { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.color = T.text3; } }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    </button>

                    <textarea ref={inputRef} rows={1} value={replyText}
                      onChange={e => { setReplyText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); attachedFile ? sendMedia() : sendReply(); } }}
                      placeholder={attachedFile ? "Add a caption (optional)..." : "Type your reply... (Enter to send, Shift+Enter for new line)"}
                      style={{ flex: 1, padding: "9px 13px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 11, color: T.text1, fontSize: 13, outline: "none", resize: "none", lineHeight: 1.5, maxHeight: 120, overflowY: "auto", transition: "border-color 0.2s" }}
                      onFocus={e => (e.target.style.borderColor = "#6366f1")}
                      onBlur={e  => (e.target.style.borderColor = T.border1)} />

                    <Btn variant="primary" onClick={attachedFile ? sendMedia : sendReply} disabled={sending || (!replyText.trim() && !attachedFile)}>
                      {sending
                        ? <div style={{ width: 13, height: 13, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                        : <Icon name="send" size={13} color="#fff" />}
                      {sending ? "Sending" : "Send"}
                    </Btn>
                  </div>
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 5 }}>
                    Replying as <strong style={{ color: T.text2 }}>you</strong> · AI is paused for this contact
                  </div>
                </div>
              ) : (
                <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.border1}`, background: T.bg1, fontSize: 12, color: T.text3, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", animation: "blink 2s infinite" }} />
                  AI is handling replies automatically. Click "Take over" to reply manually.
                </div>
              )}
            </div>

            {/* ── Contact profile panel ────────────────────────── */}
            {showProfile && (
              <ContactProfilePanel
                contact={selected}
                contactName={contactName}
                token={token}
                onClose={() => setShowProfile(false)}
                onDeleteContact={() => setConfirmDelete(true)}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Modal: clear chat ───────────────────────────────────── */}
      {confirmClear && (
        <div onClick={() => setConfirmClear(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.15s ease" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 18, padding: 28, width: 360, animation: "fadeUp 0.2s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text1, marginBottom: 8 }}>Clear chat history?</div>
            <div style={{ fontSize: 13, color: T.text3, marginBottom: 22, lineHeight: 1.6 }}>
              All messages with <strong style={{ color: T.text2 }}>{contactName || cleanPhone(selected)}</strong> will be deleted. The contact profile is kept.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="danger" onClick={clearChat}>Clear chat</Btn>
              <Btn variant="ghost"  onClick={() => setConfirmClear(false)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: delete contact + chat ────────────────────────── */}
      {confirmDelete && (
        <div onClick={() => !deleting && setConfirmDelete(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.15s ease" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 18, padding: 28, width: 380, animation: "fadeUp 0.2s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f43f5e", marginBottom: 8 }}>Delete contact and chat?</div>
            <div style={{ fontSize: 13, color: T.text3, marginBottom: 22, lineHeight: 1.6 }}>
              This will permanently delete <strong style={{ color: T.text2 }}>{contactName || cleanPhone(selected)}</strong> and all their messages. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="danger" onClick={deleteContactAndChat} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, delete both"}
              </Btn>
              <Btn variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
